import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Layers, Upload, Loader2, CheckCircle2, FileDown, FileSpreadsheet } from 'lucide-react';

const POESetupPage = () => {
    const { getAuthHeaders, API } = useAuth();
    const [modulesData, setModulesData] = useState({ modules: [], qualifications: [] });
    const [loading, setLoading] = useState(true);
    const [savingMod, setSavingMod] = useState('');
    const [importing, setImporting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => { fetchModules(); }, []);

    const fetchModules = async () => {
        try {
            const res = await axios.get(`${API}/poe/modules`, { headers: getAuthHeaders() });
            setModulesData(res.data);
        } catch (e) {
            toast.error('Failed to load modules');
        } finally {
            setLoading(false);
        }
    };

    const toggleModuleQual = async (moduleRef, code) => {
        const mod = modulesData.modules.find(m => m.ref === moduleRef);
        if (!mod) return;
        const has = (mod.qualifications || []).includes(code);
        const next = has ? mod.qualifications.filter(c => c !== code) : [...(mod.qualifications || []), code];
        setSavingMod(moduleRef);
        try {
            await axios.put(`${API}/poe/modules/${moduleRef}`, { qualifications: next }, { headers: getAuthHeaders() });
            setModulesData(prev => ({ ...prev, modules: prev.modules.map(m => m.ref === moduleRef ? { ...m, qualifications: next } : m) }));
            toast.success(`${mod.name} updated`);
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
        finally { setSavingMod(''); }
    };

    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post(`${API}/poe/import`, fd, {
                headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
            });
            toast.success(res.data.message);
            fetchModules();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Import failed');
        } finally {
            setImporting(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const downloadTemplate = async () => {
        setDownloading(true);
        try {
            const res = await axios.get(`${API}/poe/import-template`, { headers: getAuthHeaders(), responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'POE_Curriculum_Template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Template downloaded');
        } catch (e) {
            toast.error('Failed to download template');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="poe-setup-page">
                <div>
                    <h1 className="font-heading text-3xl font-bold text-ica-navy">POE Setup</h1>
                    <p className="text-gray-500 dark:text-slate-400 mt-1">Map modules to qualifications and import the Portfolio of Evidence curriculum</p>
                </div>

                {/* Import / Template */}
                <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <FileSpreadsheet className="w-5 h-5 text-ica-navy dark:text-ica-light-gold" />
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Curriculum Import</h2>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
                        Download the template, fill in your curriculum, then upload it. Existing units are updated and new ones inserted (matched by Module + Unit). Columns: MasterRef, POE, ModuleRef, Module, Unit, Ref, Requirement, Dish/Technique, Preparation Methods, Cooking Methods, Week, Session, Topic, Activity.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <Button variant="outline" onClick={downloadTemplate} disabled={downloading} data-testid="poe-download-template-btn">
                            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                            Download Template
                        </Button>
                        <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" id="poe-setup-file-upload" data-testid="poe-file-input" />
                        <label htmlFor="poe-setup-file-upload">
                            <Button asChild disabled={importing} className="bg-ica-gold hover:bg-[#a8803a] cursor-pointer">
                                <span>{importing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</> : <><Upload className="w-4 h-4 mr-2" /> Upload Curriculum</>}</span>
                            </Button>
                        </label>
                    </div>
                </div>

                {/* Qualification Builder */}
                <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6" data-testid="poe-qual-builder">
                    <div className="flex items-center gap-2 mb-2">
                        <Layers className="w-5 h-5 text-ica-navy dark:text-ica-light-gold" />
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Qualification Builder</h2>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Tick the qualifications each module belongs to. A student's POE shows only the units relevant to the selected qualification.</p>
                    {loading ? (
                        <div className="py-8 text-center text-gray-400">Loading...</div>
                    ) : modulesData.modules.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-6">No modules found. Import a curriculum to get started.</p>
                    ) : (
                        <div className="space-y-4">
                            {modulesData.modules.map(m => (
                                <div key={m.ref} className="border border-gray-200 dark:border-slate-700 rounded-lg p-4" data-testid={`qual-module-${m.ref}`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <p className="font-semibold text-ica-navy dark:text-ica-light-gold">{m.name}</p>
                                            <p className="text-xs text-gray-400">{m.ref} · {m.unit_count} units</p>
                                        </div>
                                        {savingMod === m.ref && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {modulesData.qualifications.map(q => {
                                            const checked = (m.qualifications || []).includes(q.code);
                                            return (
                                                <button key={q.code} type="button" onClick={() => toggleModuleQual(m.ref, q.code)}
                                                    data-testid={`qual-toggle-${m.ref}-${q.code.replace(/\//g, '-')}`}
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${checked ? 'bg-ica-navy text-white border-ica-navy' : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300 border-gray-300 dark:border-slate-600 hover:border-ica-navy'}`}>
                                                    {checked && <CheckCircle2 className="w-3 h-3 mr-1 inline" />}{q.title} ({q.code})
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default POESetupPage;
